import { Box, Card, Typography, useTheme } from '@mui/material';
import { BenefitDTOBenefitEnum, BikeleasingBenefitDetailsDTO } from 'probonio-shared-ui/api';
import { BikeleasingBIcon } from 'probonio-shared-ui/component/icon';
import { CircularProgressBar } from 'probonio-shared-ui/component/progress';
import { apis, useEmployeeQuery } from 'probonio-shared-ui/module/api';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { BackIconButton } from '../../component/button/BackIconButton';
import { MobileLayout } from '../../component/layout';
import { AppToolbar } from '../../component/layout/AppToolbar';
import { BikeleasingContractDetails } from '../../module/benefit/bikeleasing/BikeleasingContractDetails';
import { useActiveBenefitsQuery } from '../../module/bikeleasing/useActiveBenefitsQuery';
import { HighlightGuideButton } from '../../module/guiding';
import { useBackNavigator } from '../../module/navigation/BackNavigator';

export const BikeleasingContractDetailsPage: React.FC = () => {
  const { contractId } = useParams();
  const { back } = useBackNavigator();
  const theme = useTheme();
  const navigate = useNavigate();
  const { data: activeBenefits } = useActiveBenefitsQuery();
  const { t } = useTranslation('benefitModule');
  const benefit = BenefitDTOBenefitEnum.Bikeleasing.toLowerCase();

  const bikeleasingBenefitDetails = useMemo(
    () =>
      activeBenefits?.benefits.find(activeBenefit => activeBenefit.benefit === BenefitDTOBenefitEnum.Bikeleasing)?.details as
        | BikeleasingBenefitDetailsDTO
        | undefined,
    [activeBenefits?.benefits],
  );

  const { data: bikeleasingContract } = useEmployeeQuery(
    ['benefit', 'bikeleasing', 'contract', contractId!],
    params => apis.bikeLeasing.getBikeleasingContractById({ ...params, contractId: contractId! }).then(res => res.data),
    { enabled: !!contractId },
  );

  const content = useMemo(() => {
    if (bikeleasingContract && bikeleasingBenefitDetails?.bikeleasingCondition) {
      return <BikeleasingContractDetails condition={bikeleasingBenefitDetails.bikeleasingCondition} contract={bikeleasingContract} />;
    }
    return (
      <Box width="100%" height="100%" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgressBar />
      </Box>
    );
  }, [bikeleasingBenefitDetails, bikeleasingContract]);

  useEffect(() => {
    if (!contractId) {
      back();
    }
  }, [back, contractId]);

  return (
    <MobileLayout
      iosOverloadScrollBackground={theme.palette.background.paper}
      header={
        <Card sx={{ borderRadius: 0, boxShadow: 0 }}>
          <AppToolbar
            title={
              <Box display="flex" flexDirection="row" alignItems="center" gap={0.5}>
                <BikeleasingBIcon fontSize="extraLarge" />
                <Typography variant="extraBig">{t('benefit.BIKELEASING.name')}</Typography>
              </Box>
            }
            backButton={<BackIconButton onClick={back} />}
            actionButton={
              <HighlightGuideButton
                name={t('benefit.BIKELEASING.name')}
                header={t('benefit.BIKELEASING.firstHelpGuide.header')}
                text={t('benefit.BIKELEASING.firstHelpGuide.text')}
                onClick={() => navigate(`/${benefit}/info`)}
              />
            }
          />
          <Typography variant="body1" textAlign="center" sx={{ mb: 0.5 }}>
            {t('benefit.BIKELEASING.contractDetails', { contractNumber: bikeleasingContract?.contractNumber ?? '-' })}
          </Typography>
        </Card>
      }
    >
      {content}
    </MobileLayout>
  );
};
